<template>
  <ak-card-header class="flex">
    <ak-select
      v-model="selectedProject"
      :inline-edit="true"
      class="flex__item color-grey text-bold h4"
      :max-height="200"
      placeholder="Select a Project"
      :options="projects"
      :allow-empty="false"
      track-by="_id"
      label="title"
      @select="selectProject"
    />
    <ak-tooltip :placement="'left'" :has-arrow="false">
      <template #trigger>
        <ak-button
          circle
          size="small"
          color="transparent"
          @click="changeWidget()"
        >
          <i class="color-grey-light icon-cog" />
        </ak-button>
      </template>
      Change Widget
    </ak-tooltip>
  </ak-card-header>
  <ak-card-body class="overflow-y flex flex--column">
    <ak-project-breakdown
      :selected-project="selectedProject"
      :stats-summary="statsSummary"
      :project-overview="projectOverview"
      :size="size"
      :loading="loading"
    />
    <div></div>
  </ak-card-body>
</template>

<script>
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { defineComponent, ref, computed, onMounted } from 'vue'
import Card from '@akerolabs/akero-vue/src/components/cards/Card.vue'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import ProjectBreakdown from './components/ProjectBreakdown.vue'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'

export default defineComponent({
  name: 'AkWidgetProjectOverview',

  components: {
    [Card.name]: Card,
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    [ProjectBreakdown.name]: ProjectBreakdown,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description change widget function
     */
    changeWidget: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {}
     * @description current widget name
     */
    currentWidget: {
      default: '',
      type: String,
    },
    /**
     * @type {Function}
     * @description Update widget state
     */
    setAccountState: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {Function}
     * @description Get widget state
     */
    getAccountState: {
      default: () => ({}),
      type: Function,
    },
    /**
     * @type {}
     * @description location of widget on dashboard
     */
    region: {
      default: '',
      type: String,
    },
    size: {
      default: '',
      type: String,
    },
  },
  setup(props) {
    const projects = ref([])
    const selectedProject = ref([])
    const statsSummary = ref([])
    const projectOverview = ref([])
    const loading = ref(true)

    const updateProject = (project) => {
      props.setAccountState(
        'widgets.data.' + props.region + '._id',
        project._id,
      )
    }

    const selectProject = async (selected) => {
      loading.value = true
      const found = projects.value.find((i) => i._id === selected._id)
      if (found) selectedProject.value = found
      updateProject(selectedProject.value)
      await fetchStats(selectedProject.value)
      await fetchProjectOverview(selectedProject.value)
    }

    const fetchProjects = async () => {
      let projectsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/campaigns', projectsRequest.value)
        projectsData = data.data
      } catch (ex) {
        projectsData = []
        displayError(ex)
      }
      projects.value = projectsData
    }

    const fetchStats = async (project) => {
      let statsData = []
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get('/campaigns/stats/' + project._id)
        statsData = {
          pages: data.data.appsWithHidden,
          forms: data.data.appsWithEmbedWithHidden - data.data.appsWithHidden,
          captures: data.data.captureCount,
        }
      } catch (ex) {
        statsData = []
        displayError(ex)
      }
      statsSummary.value = statsData
    }

    const fetchProjectOverview = async (project) => {
      let overviewData = []
      let visits
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(
          '/analytics/stats/campaign_overview',
          {
            params: {
              account: accountObj()._id,
              agency: agencyObj()._id,
              campaign: project._id,
            },
            visits: visits === true,
          },
        )
        overviewData = data.data
      } catch (ex) {
        console.log(ex)
        displayError(ex)
      }
      projectOverview.value = overviewData
      loading.value = false
    }

    const projectsRequest = computed(() => {
      let params = {
        account: accountObj()._id,
        agency: agencyObj()._id,
        deleted: JSON.stringify({ $ne: true }),
        archived: JSON.stringify({ $ne: true }),
        per_page: 0,
      }
      return {
        params,
      }
    })

    const setInitialProject = async () => {
      let desiredProject = props.getAccountState(`widgets.data.${props.region}`)
      const found = projects.value.find(
        (i) => desiredProject && i._id === desiredProject._id,
      )
      if (found) {
        selectedProject.value = found
        return
      } else {
        selectedProject.value = projects.value[0]
      }
    }

    onMounted(async () => {
      await fetchProjects()
      await setInitialProject()
      await Promise.all([
        fetchStats(selectedProject.value),
        fetchProjectOverview(selectedProject.value),
      ])
    })

    return {
      selectProject,
      selectedProject,
      projects,
      statsSummary,
      projectOverview,
      loading,
    }
  },
})
</script>
